import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useHistory, useParams } from 'react-router-dom';
import { useController } from '@rest-hooks/react';

import ProductResource from 'resources/organization/ProductResource';
import EventResource from 'resources/organization/EventResource';
import EventProductListingResource from 'resources/organization/EventProductListingResource';

import UnsavedChangesModal from 'components/UnsavedChangesModal';
import DeleteModal from 'components/DeleteModal';

import { useToast } from 'utils/context/ToastContext';
import { useError } from 'utils/useErrorController';
import { updateDraggableList } from 'utils/updateDraggableList';

import EventProductsList from './EventProductsList';
import SelectStoreProductModal from './SelectStoreProductModal';

import FormSection from 'ui/FormSection';
import Button from 'ui/Button';
import Label from 'ui/text/Label';

import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import StorefrontIcon from '@mui/icons-material/Storefront';

const EventProducts = ({ event, onEventChange, unsavedChanges, setUnsavedChanges }) => {
    const { id, event_products = [], over } = event;

    const [unsavedChangesModal, setUnsavedChangesModal] = useState(false);
    const [deletingProduct, setDeletingProduct] = useState(null);
    const [redirectTarget, setRedirectTarget] = useState(null);
    const [eligibleProducts, setEligibleProducts] = useState([]);
    const [selectingStoreProduct, setSelectingStoreProduct] = useState(false);
    const [editingListing, setEditingListing] = useState(null);

    const params = useParams();
    const organizationId = parseInt(params.organizationId);

    const history = useHistory();
    const { fetch } = useController();
    const { handleError } = useError();
    const { showToast } = useToast();

    const handleRedirect = (e, target) => {
        e.preventDefault();
        if (unsavedChanges) {
            setUnsavedChangesModal(true);
            setRedirectTarget(target);
        } else {
            history.push(`/organizations/${organizationId}/perks${target}`);
        }
    };

    const handleDiscardChanges = () => {
        setUnsavedChangesModal(false);
        setUnsavedChanges(false);
        setRedirectTarget(null);
        history.push(`/organizations/${organizationId}/perks${redirectTarget}`);
    };

    const handleDeleteRequest = (product) => {
        setDeletingProduct(product);
    };

    const handleDragEnd = async (result) => {
        if (!result.destination) return;

        const savedEvent = event;
        try {
            const startIndex = result.source.index;
            const endIndex = result.destination.index;
            const listingId = +result.draggableId;

            updateDraggableList(event_products, startIndex, endIndex, (reordered) =>
                onEventChange({ ...event, event_products: reordered }));

            await fetch(EventResource.update(), { organization_id: organizationId, id }, { listing_id: listingId, position: endIndex });
        } catch (error) {
            onEventChange(savedEvent);
            handleError(error);
        }
    };

    const handleDelete = async () => {
        try {
            if (deletingProduct.storefront_visible) {
                await fetch(EventProductListingResource.delete(), {
                    id: deletingProduct.listing_id,
                    event_id: id,
                    organization_id: organizationId,
                });
            } else {
                await fetch(ProductResource.delete(), { id: deletingProduct.product_id, organization_id: organizationId });
            }
            onEventChange({ ...event, event_products: event_products.filter(product => product.listing_id !== deletingProduct.listing_id) });
            setDeletingProduct(null);
            showToast('success', deletingProduct.storefront_visible ? 'Product removed from event' : 'Product deleted');
        } catch (error) {
            handleError(error);
        }
    };

    const openStoreSelector = async () => {
        try {
            const products = await fetch(EventProductListingResource.list(), {
                event_id: id,
                organization_id: organizationId,
            });
            setEligibleProducts(products);
            setSelectingStoreProduct(true);
        } catch (error) {
            handleError(error);
        }
    };

    const handleListingSave = async ({ productId, quantity, variantAllocations }) => {
        try {
            // Variant products allocate per SKU (SELL06); the listing-level
            // quantity stays for variant-less products.
            const body = variantAllocations
                ? { variant_allocations: variantAllocations }
                : { allocation_quantity: quantity };
            let saved;
            if (editingListing) {
                saved = await fetch(EventProductListingResource.update(), {
                    id: editingListing.listing_id,
                    event_id: id,
                    organization_id: organizationId,
                }, body);
                onEventChange({
                    ...event,
                    event_products: event_products.map((item) => item.listing_id === saved.listing_id ? saved : item),
                });
            } else {
                saved = await fetch(EventProductListingResource.create(), {
                    event_id: id,
                    organization_id: organizationId,
                }, { product_id: productId, ...body });
                onEventChange({ ...event, event_products: [...event_products, saved] });
            }
            setSelectingStoreProduct(false);
            setEditingListing(null);
            showToast('success', 'Event product saved');
        } catch (error) {
            handleError(error);
        }
    };

    return (
        <FormSection title={"Products"}>
            { id ? (
                <>
                    <EventProductsList
                        products={event_products}
                        disabled={over}
                        handleDeleteRequest={handleDeleteRequest}
                        handleEditQuantity={setEditingListing}
                        handleDragEnd={handleDragEnd}
                        handleRedirect={handleRedirect} />

                    <Button
                        onClick={openStoreSelector}
                        secondary
                        fullwidth
                        icon={StorefrontIcon}
                        disabled={over} >
                            Select from Store
                    </Button>

                    <Button
                        onClick={(e) => handleRedirect(e, `/new?eventId=${id}&category=merch`)}
                        secondary
                        fullwidth
                        icon={AddCircleOutlineIcon}
                        disabled={over} >
                            Event Exc. Product
                    </Button>
                </>
            ) : (
                <Label secondary padded>
                    Product creation will be enabled after saving the event.
                </Label>
            )}

            {unsavedChangesModal &&
                <UnsavedChangesModal
                    open={unsavedChangesModal}
                    setOpen={setUnsavedChangesModal}
                    onConfirm={() => handleDiscardChanges()} />}

            <DeleteModal
                deletingObject={deletingProduct}
                objectCategory={deletingProduct?.storefront_visible ? 'product from this event' : 'product'}
                setOpen={setDeletingProduct}
                onConfirm={() => handleDelete()} />

            {(selectingStoreProduct || editingListing) &&
                <SelectStoreProductModal
                    products={editingListing ? [editingListing] : eligibleProducts}
                    listing={editingListing}
                    onClose={() => { setSelectingStoreProduct(false); setEditingListing(null); }}
                    onSave={handleListingSave} />}
        </FormSection>
    );
};

EventProducts.propTypes = {
    event: PropTypes.object,
    onEventChange: PropTypes.func,
    unsavedChanges: PropTypes.bool,
    setUnsavedChanges: PropTypes.func,
};

export default EventProducts;
